import UserProfile from "@/domain/entities/user/UserProfile";
import AgendaItemAttendee from "./AgendaItemAttendee";
import AgendaItemLocation from "./AgendaItemLocation";
import AgendaItemOrganizer from "./AgendaItemOrganizer";
import { ResponseStatus } from "./AgendaItemResponseStatus";
import AgendaOnlineMeeting from "./AgendaOnlineMeeting";

export type AgendaItemImportance = "low" | "normal" | "high";

export type AgendaItemSensitivity =
    | "normal"
    | "personal"
    | "private"
    | "confidential";

export type AgendaItemShowAs =
    | "free"
    | "tentative"
    | "busy"
    | "oof"
    | "workingElsewhere"
    | "unknown";

export type AgendaItemType =
    | "singleInstance"
    | "occurrence"
    | "exception"
    | "seriesMaster";

type AgendaItemProps = {
    id: string;
    subject: string;
    start: Date;
    end: Date;
    location?: AgendaItemLocation;
    attendees: AgendaItemAttendee[];
    organizer?: AgendaItemOrganizer;
    categories: string[];
    body?: string;
    bodyPreview?: string;
    onlineMeeting?: AgendaOnlineMeeting;
    isAllDay: boolean;
    isCancelled: boolean;
    importance: AgendaItemImportance;
    sensitivity: AgendaItemSensitivity;
    showAs: AgendaItemShowAs;
    type: AgendaItemType;
    webLink: string;
    createdDateTime: Date;
    lastModifiedDateTime: Date;
};

export default class AgendaItem {
    id: string;

    subject: string;

    start: Date;

    end: Date;

    location?: AgendaItemLocation;

    attendees: AgendaItemAttendee[];

    organizer?: AgendaItemOrganizer;

    categories: string[];

    body?: string;

    bodyPreview?: string;

    onlineMeeting?: AgendaOnlineMeeting;

    isAllDay: boolean;

    isCancelled: boolean;

    importance: AgendaItemImportance;

    sensitivity: AgendaItemSensitivity;

    showAs: AgendaItemShowAs;

    type: AgendaItemType;

    webLink: string;

    createdDateTime: Date;

    lastModifiedDateTime: Date;

    constructor({
        id,
        subject,
        start,
        end,
        location,
        attendees,
        organizer,
        categories,
        body,
        bodyPreview,
        onlineMeeting,
        isAllDay,
        isCancelled,
        importance,
        sensitivity,
        showAs,
        type,
        webLink,
        createdDateTime,
        lastModifiedDateTime,
    }: AgendaItemProps) {
        this.id = id;
        this.subject = subject;
        this.start = start;
        this.end = end;
        this.location = location;
        this.attendees = attendees;
        this.organizer = organizer;
        this.categories = categories;
        this.body = body;
        this.bodyPreview = bodyPreview;
        this.onlineMeeting = onlineMeeting;
        this.isAllDay = isAllDay;
        this.isCancelled = isCancelled;
        this.importance = importance;
        this.sensitivity = sensitivity;
        this.showAs = showAs;
        this.type = type;
        this.webLink = webLink;
        this.createdDateTime = createdDateTime;
        this.lastModifiedDateTime = lastModifiedDateTime;
    }

    isAllDayEvent() {
        return (
            this.isAllDay ||
            (this.start.getHours() === 0 &&
                this.start.getMinutes() === 0 &&
                this.start.getSeconds() === 0 &&
                this.end.getHours() === 0 &&
                this.end.getMinutes() === 0 &&
                this.end.getSeconds() === 0) // Both start and end times are at 00:00:00
        );
    }

    isOnSeveralDays() {
        const lastDay = this.isAllDayEvent()
            ? new Date(this.end.getTime() - 1)
            : this.end;

        return (
            this.start.getFullYear() !== lastDay.getFullYear() ||
            this.start.getMonth() !== lastDay.getMonth() ||
            this.start.getDate() !== lastDay.getDate()
        );
    }

    getUserAcceptedStatus(user: UserProfile): ResponseStatus {
        if (this.userIsOrganizer(user)) {
            return ResponseStatus.Accepted;
        }

        const userEmail = user.profile.email.toLowerCase();
        const userAsAttendee = this.attendees.find(
            (attendee) =>
                attendee.emailAddress.address.toLowerCase() === userEmail,
        );

        if (userAsAttendee && userAsAttendee.status) {
            return userAsAttendee.status.response;
        }

        return ResponseStatus.None;
    }

    userIsOrganizer(user: UserProfile): boolean {
        return (
            this.organizer?.emailAddress.address.toLowerCase() ===
            user.profile.email.toLowerCase()
        );
    }

    isNewEvent(): boolean {
        return this.id === "";
    }
}
